import {Action, Selector, State, StateContext, Store} from "@ngxs/store";
import {CompoApp} from "../../services/CompoApp";
import {ChecklistTemplateEntryReorderAction} from "./checklist-template-entry-reorder.action";
import {ChecklistTemplateDetailXsState} from "../checklist-template/checklist-template-detail-xs.state";
import {finalize, from, of, switchMap, throwError} from "rxjs";
import {ChecklistTemplateDetailAction} from "../checklist-template/checklist-template-detail.action";
import {Observable} from "rxjs/internal/Observable";
import {Injectable} from "@angular/core";

import {ResetStateDefaults} from "../base-action-class.model";
import {ChecklistTemplateEntryRequestService} from "../../services/request/checklist-template-entry-request.service";


interface ChecklistTemplateEntryReorderStateModel {
  draggedBlockIndex: number;
  targetIndex: number;
  dragInProgress: boolean;
  inProgress: boolean;
}

const getDefaults = (): ChecklistTemplateEntryReorderStateModel => ({
  draggedBlockIndex: null,
  targetIndex: null,
  dragInProgress: false,
  inProgress: false
});

@State<ChecklistTemplateEntryReorderStateModel>({
  name: 'checklistTemplateEntryReorder',
  defaults: getDefaults()
})
@Injectable()
export class ChecklistTemplateEntryReorderXsState {

  //region CONSTRUCTOR
  constructor(
    private app: CompoApp,
    private store: Store,
    private checklistTemplateEntryRequestService: ChecklistTemplateEntryRequestService
  ) {}
  //endregion

  //region SELECTOR
  @Selector()
  public static draggedBlockTargetIndex(state: ChecklistTemplateEntryReorderStateModel): number | null {
    return state.targetIndex;
  }

  @Selector()
  public static dragInProgress(state: ChecklistTemplateEntryReorderStateModel): boolean {
    return state.dragInProgress;
  }

  @Selector()
  public static inProgress(state: ChecklistTemplateEntryReorderStateModel): boolean {
    return state.inProgress;
  }
  //endregion

  //region ACTION
  @Action(ChecklistTemplateEntryReorderAction.DragStarted)
  public dragStarted(
    ctx: StateContext<ChecklistTemplateEntryReorderStateModel>,
    action: ChecklistTemplateEntryReorderAction.DragStarted
  ): void {
    const state: ChecklistTemplateEntryReorderStateModel = ctx.getState();

    if (state.inProgress) {
      throw new Error('Cannot start drag! Reorder is in progress!');
    }

    if (action.blockIndex == null) {
      throw new Error('Block index cannot be null!');
    }

    ctx.patchState({
      draggedBlockIndex: action.blockIndex,
      targetIndex: null,
      dragInProgress: true
    });
  }

  @Action(ChecklistTemplateEntryReorderAction.DragIndexChanged)
  public dragIndexChanged(
    ctx: StateContext<ChecklistTemplateEntryReorderStateModel>,
    action: ChecklistTemplateEntryReorderAction.DragIndexChanged
  ): void {
    const state: ChecklistTemplateEntryReorderStateModel = ctx.getState();

    if (state.inProgress) {
      throw new Error('Cannot set target index! Reorder request is in progress!');
    }

    if (!state.dragInProgress) {
      throw new Error('Cannot set target index! Drag is not started!');
    }

    if (action.newIndex == null) {
      throw new Error('New index cannot be null!');
    }

    ctx.patchState({
      targetIndex: action.newIndex,
    });
  }

  @Action(ChecklistTemplateEntryReorderAction.DragEnded)
  public dragEnded(
    ctx: StateContext<ChecklistTemplateEntryReorderStateModel>
  ): Observable<void> {
    const state: ChecklistTemplateEntryReorderStateModel = ctx.getState();

    if (!state.dragInProgress) {
      throw new Error('Cannot end drag! Drag is not started!');
    }

    if (state.targetIndex == null || state.targetIndex === state.draggedBlockIndex) {
      ctx.patchState(getDefaults());
      return of();
    }

    const checklistTemplateEntryIdsInBlocks: string[][] = this.store.selectSnapshot(
      ChecklistTemplateDetailXsState.checklistTemplateEntryIdsInBlocks
    );

    const checklistTemplateId: string = this.store.selectSnapshot(
      ChecklistTemplateDetailXsState.id
    );

    if (checklistTemplateEntryIdsInBlocks[state.draggedBlockIndex].length === 0
      || checklistTemplateEntryIdsInBlocks[state.targetIndex].length === 0
    ) {
      throw new Error('Checklist template entry blocks cannot be empty!');
    }

    const fromBlockFirstId: string = checklistTemplateEntryIdsInBlocks[state.draggedBlockIndex][0];
    const toBlockFirstId: string = checklistTemplateEntryIdsInBlocks[state.targetIndex][0];

    ctx.patchState({
      inProgress: true
    });

    this.store.dispatch(new ChecklistTemplateDetailAction.ChecklistTemplateEntryBlockDragged(
      state.draggedBlockIndex,
      state.targetIndex
    ));

    return from(this.checklistTemplateEntryRequestService.reorderChecklistEntries(
      checklistTemplateId,
      fromBlockFirstId,
      toBlockFirstId
      ))
      .pipe(
        switchMap( () => {
            return this.store.dispatch(new ChecklistTemplateDetailAction.ChecklistTemplateEntryReordered());
          }
        ),
        finalize(() => ctx.patchState({
          inProgress: false,
          dragInProgress: false,
          draggedBlockIndex: null,
          targetIndex: null
        }))
      );
  }

  @Action([
    ChecklistTemplateEntryReorderAction.MoveBack,
    ChecklistTemplateEntryReorderAction.MoveForward
  ])
  public moveEntry(
    ctx: StateContext<ChecklistTemplateEntryReorderStateModel>,
    action: ChecklistTemplateEntryReorderAction.MoveBack | ChecklistTemplateEntryReorderAction.MoveForward
  ): Observable<void> {
    const state: ChecklistTemplateEntryReorderStateModel = ctx.getState();

    if (state.inProgress) {
      return throwError(() =>
        new Error('Checklist template request is in progress!')
      );
    }

    if (!action.id) {
      return throwError(() =>
        new Error('Checklist template entry id cannot be null!')
      );
    }

    const checklistTemplateId: string = this.store.selectSnapshot(ChecklistTemplateDetailXsState.id);

    if (checklistTemplateId == null) {
      return throwError(() =>
        new Error('Cannot move checklist template entry! State returned null id for checklist template!')
      );
    }

    let request: Promise<void>;

    if (action instanceof ChecklistTemplateEntryReorderAction.MoveBack) {
      request = this.checklistTemplateEntryRequestService.moveEntryBack(
        checklistTemplateId,
        action.id
      );
    }

    if (action instanceof ChecklistTemplateEntryReorderAction.MoveForward) {
      request = this.checklistTemplateEntryRequestService.moveEntryForward(
        checklistTemplateId,
        action.id
      );
    }

    ctx.patchState({
      inProgress: true
    });

    return from(request)
      .pipe(
        switchMap(() => {
          return this.store.dispatch(new ChecklistTemplateDetailAction.ChecklistTemplateEntryReordered());
        }),
        finalize(() => ctx.patchState({inProgress: false}))
      );
  }

  @Action([
    ChecklistTemplateEntryReorderAction.ResetState,
    ResetStateDefaults
  ])
  public resetState(
    ctx: StateContext<ChecklistTemplateEntryReorderStateModel>
  ): void {
    ctx.setState({...getDefaults()});
  }
  //endregion
}
